import z from 'zod'

import {
  WORKSPACE_PANEL_NAMES,
  WorkspacePanelName,
} from './workspacePanelNames'

/**
 * Default `minWidthRem` applied when a panel omits the field. Mirrors the
 * historical 180px floor at the 16px root font size.
 */
export const DEFAULT_MIN_REM = 11

/**
 * A single panel inside a workflow step's `workspaceLayout`.
 *
 * Sizing model: `weight` is a dimensionless ratio used to distribute the
 * container's available width across all visible panels (after reserving
 * each panel's `minWidthRem`). The trailing panel renders as a CSS flex
 * remainder, so its `weight` only participates in *fresh-distribute*
 * allocations (initial mount, pre-drag container resize, pre-drag collapse
 * toggle). After the first user drag, the trailing panel becomes a pure
 * runtime-slack absorber and its `weight` no longer participates in
 * re-distribution.
 *
 * "Never grow" is intentionally unsupported -- use a low `weight` if you
 * want a panel that hugs its minimum at typical container widths.
 *
 * Strict parsing: unknown keys are rejected. Legacy `defaultWidth` /
 * `minWidth` (px) configs will fail to parse so callers see an explicit
 * migration error rather than silent reinterpretation.
 */
const workspacePanelSchema = z
  .object({
    id: z.string().optional(),
    component: z.enum(WORKSPACE_PANEL_NAMES),
    weight: z.number().finite().positive().default(1),
    minWidthRem: z.number().finite().positive().default(DEFAULT_MIN_REM),
    /**
     * Panels sharing a non-empty `collapseGroup` collapse together. When the
     * group is collapsed, the entire group is replaced by a single 40px
     * strip with a chevron toggle. Panels in a group must be contiguous in
     * `panels[]`. Undefined = not collapsible; empty strings are rejected.
     */
    collapseGroup: z.string().min(1).optional(),
  })
  .strict()

export const workspaceLayoutSchema = z
  .object({
    panels: z.array(workspacePanelSchema).min(1),
  })
  .strict()
  .superRefine((value, ctx) => {
    const seen = new Set<string>()
    value.panels.forEach((panel, index) => {
      const key = panel.id ?? panel.component
      if (seen.has(key)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['panels', index],
          message: `Duplicate panel id/component: ${key}`,
        })
      }
      seen.add(key)
    })

    // The trailing panel acts as the flex stretch element in
    // ResizableNPanelLayout and is never given a collapse handle, so a
    // `collapseGroup` on the last panel would be silently dropped at
    // render time. Reject at validation time instead.
    if (value.panels.length > 0) {
      const lastIdx = value.panels.length - 1
      const lastPanel = value.panels[lastIdx]
      if (lastPanel.collapseGroup !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['panels', lastIdx, 'collapseGroup'],
          message:
            'collapseGroup is not supported on the trailing (flex) panel.',
        })
      }
    }

    // Contiguity: panels sharing a `collapseGroup` must appear in a single
    // unbroken run. A group like [A:docs, B:other, C:docs] has no clean
    // collapsed representation.
    const groupFirstSeen = new Map<string, number>()
    const groupLastSeen = new Map<string, number>()
    value.panels.forEach((panel, index) => {
      if (!panel.collapseGroup) return
      if (!groupFirstSeen.has(panel.collapseGroup)) {
        groupFirstSeen.set(panel.collapseGroup, index)
      }
      groupLastSeen.set(panel.collapseGroup, index)
    })
    for (const [group, firstIdx] of groupFirstSeen) {
      const lastIdx = groupLastSeen.get(group)!
      for (let i = firstIdx; i <= lastIdx; i++) {
        if (value.panels[i].collapseGroup !== group) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['panels', i],
            message: `Discontiguous collapseGroup "${group}": panels with the same group must be adjacent.`,
          })
          break
        }
      }
    }
  })

export type WorkspaceLayoutConfig = z.infer<typeof workspaceLayoutSchema>
export type WorkspaceLayoutPanel = WorkspaceLayoutConfig['panels'][number]

// Re-exported via separate type alias to avoid the barrel-file lint rule.
export type WorkspaceLayoutPanelName = WorkspacePanelName

/**
 * Safely parses a step's `workspaceLayout` field. Returns the config if
 * valid, or undefined if absent/invalid. Logs validation errors to the
 * console so one bad Rolodex workflow entry doesn't crash the workflow
 * list.
 *
 * Called by `mapEntryToLibraWorkflow` in `workflowStepsApi.ts` against each
 * step's `workspaceLayout` (not against `globalContext`).
 *
 * Mirrors `parseLifecycleConfig` in `features/libra/lifecycle/lifecycleTypes.ts`.
 */
export function parseWorkspaceLayout(
  container: Record<string, unknown>
): WorkspaceLayoutConfig | undefined {
  const raw = container.workspaceLayout
  if (!raw || typeof raw !== 'object') return undefined
  const result = workspaceLayoutSchema.safeParse(raw)
  if (!result.success) {
    console.warn(
      '[Libra] workspaceLayout failed validation:',
      result.error.issues
    )
    return undefined
  }
  return result.data
}
